import React, { useState } from "react";
import { Upload, File, AlertCircle } from "lucide-react";
import api from "../services/apiService";
import { GRADE_SUBMIT } from "../services/apiRoutes";

export async function submitGrade(questionPdf, answerPdf) {
  const formData = new FormData();
  formData.append("question_pdf", questionPdf);
  formData.append("answer_pdf", answerPdf);

  const response = await api.post(GRADE_SUBMIT, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

function GradePage() {
  const [questionPdf, setQuestionPdf] = useState(null);
  const [answerPdf, setAnswerPdf] = useState(null);
  const [report, setReport] = useState("No report yet");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = async (event, fileType) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed");
      return;
    }

    try {
      if (fileType === "question") {
        setQuestionPdf(file); // Store the File object directly
      } else {
        setAnswerPdf(file); // Store the File object directly
      }

      setError("");
    } catch (err) {
      setError(`Error reading file: ${err.message}`);
    }
  };

  const handleGrade = async () => {
    if (!questionPdf || !answerPdf) {
      setError("Please upload both question and answer PDFs");
      return;
    }

    setError("");
    setReport("Processing...");
    setLoading(true);

    try {
      const response = await submitGrade(questionPdf, answerPdf);

      setReport(response.report || "No report returned");
    } catch (err) {
      setError("Error: " + err.message);
      setReport("");
    } finally {
      setLoading(false);
    }
  };

  const FileUploadBox = ({ id, label, onChange, file }) => (
    <div className="mb-6">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <input
          type="file"
          id={id}
          accept=".pdf"
          onChange={onChange}
          className="hidden"
          disabled={loading}
        />
        <label
          htmlFor={id}
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          {file ? (
            <div className="flex items-center text-green-600">
              <File className="w-8 h-8 mr-2" />
              <span>PDF uploaded</span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">{label}</span>
            </>
          )}
        </label>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-6">Automatic PDF Grading</h2>

          <FileUploadBox
            id="question-pdf"
            label="Upload Question PDF"
            onChange={(e) => handleFileChange(e, "question")}
            file={questionPdf}
          />

          <FileUploadBox
            id="answer-pdf"
            label="Upload Answer PDF"
            onChange={(e) => handleFileChange(e, "answer")}
            file={answerPdf}
          />

          <button
            onClick={handleGrade}
            disabled={loading || !questionPdf || !answerPdf}
            className={`w-full py-3 px-4 rounded-lg text-white font-medium
              ${
                loading || !questionPdf || !answerPdf
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            {loading ? "Analyzing..." : "Start Analysis"}
          </button>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-2xl font-bold mb-6">Grading Report</h2>

          {error ? (
            <div className="flex items-start space-x-2 text-red-600 bg-red-50 p-4 rounded-lg">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <span>{error}</span>
            </div>
          ) : (
            <div className="prose max-w-none">{report}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GradePage;
